/**
 * RouteErrorBoundary Component
 * Wraps lazy-loaded route components to catch chunk load failures and render errors.
 * Every route element is wrapped by a boundary, so a failure in one route does not crash the entire application.
 *
 * The error state is cleared when the route (`pathname`) changes (#3822). Sibling routes of an `Outlet` whose element
 * is this boundary (same type, same position, no `key`) share ONE React instance, so without the reset the error card
 * outlived the navigation. A change of `?query` or `#hash` does not clear it: the same screen is not retried on its own.
 */

import React from 'react'

import i18n from '@platform/i18n/config'
import { useLocation } from 'react-router-dom'

import { logger } from '@/shared/utils/logger'

interface ErrorState {
  hasError: boolean
  error?: Error
  isChunkError: boolean
}

interface RouteErrorBoundaryState extends ErrorState {
  /** The route the state belongs to: when the prop moves on, the error is cleared in the same render. */
  resetKey: string
}

interface RouteErrorBoundaryProps {
  children: React.ReactNode
  routeName: string
}

interface RouteErrorBoundaryViewProps extends RouteErrorBoundaryProps {
  resetKey: string
}

const NO_ERROR: ErrorState = { hasError: false, error: undefined, isChunkError: false }

/**
 * Detects whether an error is a dynamic import / chunk loading failure.
 * These are typically network errors that resolve on reload.
 */
function isChunkLoadError(error: Error): boolean {
  const message = error.message.toLowerCase()
  /* eslint-disable local/no-hardcoded-error-fallback -- runtime bundler/browser error classification (ChunkLoadError / failed dynamic import), not backend messages */
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('chunkloaderror') ||
    error.name === 'ChunkLoadError'
  )
  /* eslint-enable local/no-hardcoded-error-fallback */
}

class RouteErrorBoundaryView extends React.Component<
  RouteErrorBoundaryViewProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryViewProps) {
    super(props)
    this.state = { ...NO_ERROR, resetKey: props.resetKey }
  }

  static getDerivedStateFromError(error: Error): ErrorState {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    }
  }

  // Clears the error in the SAME render in which the new route arrives, so the new screen is rendered once and never
  // against the previous screen's error state (a reset from `componentDidUpdate` commits that stale state first).
  static getDerivedStateFromProps(
    props: RouteErrorBoundaryViewProps,
    state: RouteErrorBoundaryState,
  ): Partial<RouteErrorBoundaryState> | null {
    if (props.resetKey === state.resetKey) return null
    return { ...NO_ERROR, resetKey: props.resetKey }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      logger.error(
        `[RouteErrorBoundary] Error in route "${this.props.routeName}":`,
        error,
        errorInfo,
      )
    }
  }

  handleRetry = () => {
    if (this.state.isChunkError) {
      // For chunk load errors, a hard reload is the most reliable fix
      window.location.reload()
    } else {
      this.setState(NO_ERROR)
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    const { isChunkError, error } = this.state

    return (
      <div
        className="min-h-[50vh] flex items-center justify-center p-8"
        data-testid="route-error-boundary"
      >
        <div className="max-w-md w-full bg-crypto-card border border-crypto-border rounded-xl p-6 shadow-lg text-center">
          <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-7 h-7 text-red-400 [html[data-theme=light]_&]:text-red-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h2 className="text-lg font-semibold text-crypto-text mb-2">
            {isChunkError
              ? i18n.t('shared:errorBoundary.chunkErrorTitle', 'Page failed to load')
              : i18n.t('shared:errorBoundary.title', 'Something went wrong')}
          </h2>

          <p className="text-crypto-muted text-sm mb-6">
            {isChunkError
              ? i18n.t(
                  'shared:errorBoundary.chunkErrorMessage',
                  'A network error prevented the page from loading. Please refresh to try again.',
                )
              : i18n.t(
                  'shared:errorBoundary.message',
                  'An unexpected error occurred. You can try again or refresh the page.',
                )}
          </p>

          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleRetry}
              className="px-4 py-2 bg-crypto-accent hover:bg-crypto-accent-light text-white text-sm font-medium rounded-lg transition-colors"
            >
              {isChunkError
                ? i18n.t('shared:errorBoundary.refreshPage', 'Refresh Page')
                : i18n.t('shared:errorBoundary.tryAgain', 'Try Again')}
            </button>

            {!isChunkError && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 border border-crypto-border text-crypto-text text-sm font-medium rounded-lg hover:bg-crypto-dark transition-colors"
              >
                {i18n.t('shared:errorBoundary.refreshPage', 'Refresh Page')}
              </button>
            )}
          </div>

          {import.meta.env.DEV && error && (
            <details className="mt-4 text-left">
              <summary className="text-xs text-crypto-muted cursor-pointer hover:text-crypto-text">
                Error details
              </summary>
              <pre className="mt-2 p-3 bg-crypto-dark rounded text-xs text-red-400 [html[data-theme=light]_&]:text-red-700 overflow-auto whitespace-pre-wrap break-all">
                {error.toString()}
              </pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}

/** The boundary every route mounts: it follows the route (`pathname`) so an error stays in the screen that failed. */
export function RouteErrorBoundary({
  children,
  routeName,
}: RouteErrorBoundaryProps): React.ReactElement {
  const { pathname } = useLocation()
  return (
    <RouteErrorBoundaryView routeName={routeName} resetKey={pathname}>
      {children}
    </RouteErrorBoundaryView>
  )
}

// Kept only so the chunk-classification table in `RouteErrorBoundary.test.tsx` can keep calling it on the exported
// name; if that test imports the view instead, drop this alias.
RouteErrorBoundary.getDerivedStateFromError = RouteErrorBoundaryView.getDerivedStateFromError
